import { lazyComponents } from './lazy-components';
import {
  allowNextGuard,
  chainRouteGuards,
  createEmbeddedRedirectGuard,
  createFeatureGuard,
  createPaidPlanGuard,
  createScheduleStartGuard,
} from './route-guards';

const EMBEDDED_REDIRECT_ROUTES = [
  {
    path: 'reagendamento/:cpf',
    component: lazyComponents.reschedule,
    names: { app: 'reschedule', cb: 'cb-reschedule', embedded: 'embedded-reschedule' },
    embeddedName: 'embedded-reschedule',
    includeQuery: true,
    cbGuard: allowNextGuard,
  },
  {
    path: 'meu-agendamento/:cpf',
    component: lazyComponents.scheduledStart,
    names: { app: 'scheduled-start', cb: 'cb-scheduled-start', embedded: 'embedded-scheduled' },
    embeddedName: 'embedded-scheduled',
    cbGuard: allowNextGuard,
  },
  {
    path: 'historico-consultas/:cpf',
    component: lazyComponents.consultationHistory,
    names: {
      app: 'consultation-history',
      embedded: 'embedded-consultation-history',
    },
    embeddedName: 'embedded-consultation-history',
  },
  {
    path: 'atendimento/sala-de-espera/:meetCode/:cpf',
    component: lazyComponents.overbookQueue,
    names: { app: 'overbook-queue', cb: 'cb-overbook-queue', embedded: 'embedded-overbook' },
    embeddedName: 'embedded-overbook',
  },
  {
    path: 'atendimento/pre-consulta/:cpf',
    component: lazyComponents.callTest,
    names: { app: 'scheduled-test', cb: 'cb-scheduled-test', embedded: 'embedded-test' },
    embeddedName: 'embedded-test',
  },
  {
    path: 'atendimento/pre-consulta-ob/:meetCode/:cpf',
    component: lazyComponents.callTestOverbook,
    names: {
      app: 'scheduled-test-overbook',
      cb: 'cb-scheduled-test-overbook',
      embedded: 'embedded-test-overbook',
    },
    embeddedName: 'embedded-test-overbook',
  },
  {
    path: 'atendimento/consulta/:meetCode/:cpf',
    component: lazyComponents.sortingMeet,
    names: { app: 'meet', cb: 'cb-meet', embedded: 'embedded-meet' },
    embeddedName: 'embedded-meet',
  },
];

const SCHEDULE_ROUTES = [
  {
    path: 'pronto-atendimento/:cpf?',
    component: lazyComponents.schedule,
    names: { app: 'schedule-pa', cb: 'cb-schedule-pa', embedded: 'embedded-schedule-pa' },
    requiresPaidPlan: true,
    cbGuard: allowNextGuard,
    appGuard: createFeatureGuard('main/hasQueue'),
  },
  {
    path: 'agendar/:cpf?',
    component: lazyComponents.schedule,
    names: { app: 'schedule-only', cb: 'cb-schedule-only', embedded: 'embedded-schedule-only' },
    requiresPaidPlan: true,
    cbGuard: allowNextGuard,
    appGuard: createFeatureGuard('main/hasSchedule'),
  },
  {
    path: 'auto/:cpf?',
    component: lazyComponents.scheduleAuto,
    names: { app: 'schedule-auto', embedded: 'embedded-schedule-auto' },
    appGuard: createFeatureGuard('main/hasQueue'),
  },
  {
    path: 'agendamento/:cpf?',
    component: lazyComponents.scheduleStart,
    names: { app: 'schedule-start', embedded: 'embedded-start' },
    appGuard: createScheduleStartGuard(),
  },
];

function buildRoute(definition, context) {
  const name = definition.names[context];
  if (!name) {
    return null;
  }

  const route = {
    path: definition.path,
    name,
    component: definition.component,
  };

  if (context === 'app') {
    if (definition.appGuard) {
      route.beforeEnter = definition.appGuard;
    } else if (definition.embeddedName) {
      route.beforeEnter = createEmbeddedRedirectGuard(definition.embeddedName, {
        includeQuery: definition.includeQuery,
      });
    }
  }

  if (context === 'cb') {
    const guards = [];
    if (definition.embeddedName) {
      guards.push(
        createEmbeddedRedirectGuard(definition.embeddedName, {
          includeQuery: definition.includeQuery,
        })
      );
    }
    if (definition.requiresPaidPlan) {
      guards.push(createPaidPlanGuard());
    } else if (definition.cbGuard) {
      guards.push(definition.cbGuard);
    }

    if (guards.length === 1) {
      route.beforeEnter = guards[0];
    } else if (guards.length > 1) {
      route.beforeEnter = chainRouteGuards(...guards);
    }
  }

  return route;
}

function buildContextRoutes(context) {
  const scheduleRoutes = SCHEDULE_ROUTES.map((definition) => buildRoute(definition, context)).filter(Boolean);
  const embeddedRedirectRoutes = EMBEDDED_REDIRECT_ROUTES.map((definition) =>
    buildRoute(definition, context)
  ).filter(Boolean);

  return [...scheduleRoutes, ...embeddedRedirectRoutes];
}

export const appPatientRoutes = buildContextRoutes('app');
export const cbPatientRoutes = buildContextRoutes('cb');
export const embeddedPatientRoutes = buildContextRoutes('embedded');
